import { cn } from "@sylviq/ui-react-web/lib/cn";
import { useId, useRef, type ReactNode } from "react";
import { useHorizontalSelection } from "@sylviq/ui-react-web/hooks/use-horizontal-selection";

export interface TabItem {
  value: string;
  label: string;
  /** Tooltip text — where a technical term belongs, never the label. */
  hint?: string;
}

export interface TabsProps {
  tabs: TabItem[];
  /** Controlled: the caller owns the active tab (state or a search param). */
  value: string;
  onValueChange: (value: string) => void;
  /** Accessible name for the tablist. */
  label: string;
  className?: string;
  children: ReactNode;
}

/**
 * Controlled tab bar and panel. Router-agnostic — the caller decides whether
 * the active tab lives in state or in the URL.
 */
export function Tabs({
  tabs,
  value,
  onValueChange,
  label,
  className,
  children,
}: TabsProps) {
  const id = useId();
  const buttons = useRef<Array<HTMLButtonElement | null>>([]);
  const strip = useRef<HTMLDivElement>(null);
  useHorizontalSelection(strip, value);
  return (
    <div className={cn("grid min-w-0 gap-4", className)}>
      <div
        role="tablist"
        aria-label={label}
        ref={strip}
        className="flex min-w-0 flex-nowrap gap-1 overflow-x-auto rounded-xl bg-[var(--muted)] p-1"
      >
        {tabs.map((tab, index) => {
          const selected = tab.value === value;
          return (
            <button
              key={tab.value}
              type="button"
              role="tab"
              id={`${id}-${tab.value}`}
              aria-controls={`${id}-panel`}
              tabIndex={selected ? 0 : -1}
              ref={node => { buttons.current[index] = node; }}
              aria-selected={selected}
              title={tab.hint}
              onClick={() => onValueChange(tab.value)}
              onKeyDown={event => {
                const step = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
                if (!step && event.key !== "Home" && event.key !== "End") return;
                event.preventDefault();
                const next = event.key === "Home" ? 0 : event.key === "End" ? tabs.length - 1 : (index + step + tabs.length) % tabs.length;
                onValueChange(tabs[next].value);
                buttons.current[next]?.focus();
              }}
              className={cn(
                "shrink-0 cursor-pointer whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus)]",
                selected
                  ? "bg-[var(--background)] text-[var(--foreground)] shadow-sm"
                  : "text-[var(--muted-foreground)] hover:text-[var(--foreground)]",
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      <div className="min-w-0" role="tabpanel" id={`${id}-panel`} aria-labelledby={`${id}-${value}`}>{children}</div>
    </div>
  );
}
